import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../contexts/AuthContext";
import { KeyRound, ArrowLeft, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { toast, Toaster } from "sonner";

/**
 * Self-service password change — usable by every signed-in role.
 * Requires re-authentication via the current password before the hash is
 * updated. The user's existing JWT remains valid after the change.
 */
export default function ChangePassword() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNext, setShowNext] = useState(false);
    const [busy, setBusy] = useState(false);

    if (!user) {
        return (
            <div className="max-w-md mx-auto p-8 text-center">
                <p className="text-stone-700">You need to be signed in to change your password.</p>
                <Button onClick={() => navigate("/login")} className="mt-4 rounded-none bg-sky-700 hover:bg-sky-800">Sign in</Button>
            </div>
        );
    }

    const strength = passwordStrength(next);
    const strengthColors = { weak: "bg-red-500", fair: "bg-amber-500", strong: "bg-emerald-500" };

    const submit = async (e) => {
        e.preventDefault();
        if (!current) return toast.error("Enter your current password");
        if (next.length < 8) return toast.error("New password must be at least 8 characters");
        if (next !== confirm) return toast.error("New passwords don't match");
        if (next === current) return toast.error("New password must be different from your current one");

        setBusy(true);
        try {
            await api.post("/auth/me/password", {
                current_password: current,
                new_password: next,
            });
            toast.success("Password updated successfully", { duration: 4000 });
            setCurrent(""); setNext(""); setConfirm("");
            setTimeout(() => navigate(homePath(user.role)), 1500);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't update password");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-[70vh] flex items-center justify-center bg-stone-50 px-4 py-8" data-testid="change-password-page">
            <Toaster position="top-center" />
            <div className="w-full max-w-md bg-white border border-stone-200 p-8 space-y-6">
                <div>
                    <button onClick={() => navigate(-1)} className="text-xs uppercase tracking-widest text-sky-700 inline-flex items-center mb-3 hover:underline" data-testid="back-link">
                        <ArrowLeft className="w-3 h-3 mr-1" />Back
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 grid place-items-center bg-sky-700 text-stone-50">
                            <KeyRound className="w-5 h-5" />
                        </div>
                        <div>
                            <div className="label-eyebrow text-sky-700">Security</div>
                            <h1 className="font-serif text-2xl text-stone-900">Change password</h1>
                        </div>
                    </div>
                    <p className="text-xs text-stone-600 mt-3">
                        Signed in as <strong>{user.email}</strong>. Enter your current password, then choose a strong new one.
                    </p>
                </div>

                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-stone-500">Current password</Label>
                        <div className="relative mt-1">
                            <Input
                                type={showCurrent ? "text" : "password"}
                                value={current}
                                onChange={(e) => setCurrent(e.target.value)}
                                autoComplete="current-password"
                                className="rounded-none border-stone-300 pr-10"
                                data-testid="current-password"
                                required
                            />
                            <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500" tabIndex={-1}>
                                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs uppercase tracking-widest text-stone-500">New password</Label>
                        <div className="relative mt-1">
                            <Input
                                type={showNext ? "text" : "password"}
                                value={next}
                                onChange={(e) => setNext(e.target.value)}
                                autoComplete="new-password"
                                minLength={8}
                                className="rounded-none border-stone-300 pr-10"
                                data-testid="new-password"
                                required
                            />
                            <button type="button" onClick={() => setShowNext(!showNext)} className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500" tabIndex={-1}>
                                {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                        {next && (
                            <div className="mt-2" data-testid="strength-bar">
                                <div className="h-1 bg-stone-200 overflow-hidden">
                                    <div className={`h-full transition-all ${strengthColors[strength.level]}`} style={{ width: `${strength.percent}%` }} />
                                </div>
                                <div className="flex justify-between text-[10px] mt-1 uppercase tracking-widest">
                                    <span className={`${strength.level === "weak" ? "text-red-700" : strength.level === "fair" ? "text-amber-700" : "text-emerald-700"} font-semibold`}>{strength.label}</span>
                                    <span className="text-stone-400">{next.length} chars</span>
                                </div>
                                <ul className="text-[11px] text-stone-600 mt-2 space-y-0.5">
                                    <Hint ok={next.length >= 8}>At least 8 characters</Hint>
                                    <Hint ok={/[a-z]/.test(next) && /[A-Z]/.test(next)}>Mix of upper &amp; lower case</Hint>
                                    <Hint ok={/\d/.test(next)}>Contains a number</Hint>
                                    <Hint ok={/[^a-zA-Z0-9]/.test(next)}>Contains a symbol</Hint>
                                </ul>
                            </div>
                        )}
                    </div>

                    <div>
                        <Label className="text-xs uppercase tracking-widest text-stone-500">Confirm new password</Label>
                        <Input
                            type={showNext ? "text" : "password"}
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            autoComplete="new-password"
                            className={`rounded-none border-stone-300 mt-1 ${confirm && confirm !== next ? "border-red-400" : ""}`}
                            data-testid="confirm-password"
                            required
                        />
                        {confirm && confirm !== next && <div className="text-[11px] text-red-700 mt-1">Doesn&apos;t match</div>}
                    </div>

                    <Button type="submit" disabled={busy} className="w-full rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="submit-password">
                        {busy ? "Updating…" : <><ShieldCheck className="w-4 h-4 mr-2" />Update password</>}
                    </Button>
                </form>

                <div className="text-[11px] text-stone-500 leading-relaxed border-t border-stone-200 pt-4">
                    <strong>Tip:</strong> use a passphrase like <span className="font-mono">Hpl@Pharmacy-2026!</span> rather than a single word. Long passphrases are harder to crack and easier to remember.
                </div>
            </div>
        </div>
    );
}

function passwordStrength(pw) {
    let score = 0;
    if (pw.length >= 8) score++;
    if (pw.length >= 12) score++;
    if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
    if (/\d/.test(pw)) score++;
    if (/[^a-zA-Z0-9]/.test(pw)) score++;
    if (score <= 2) return { level: "weak", label: "Weak", percent: 33 };
    if (score === 3 || score === 4) return { level: "fair", label: "Fair", percent: 66 };
    return { level: "strong", label: "Strong", percent: 100 };
}

function homePath(role) {
    if (role === "admin" || role === "editor") return "/admin";
    if (role === "employee") return "/employee";
    if (role === "distributor") return "/distributor";
    if (role === "doctor") return "/doctor";
    return "/";
}

const Hint = ({ ok, children }) => (
    <li className={`flex items-center gap-1 ${ok ? "text-emerald-700" : "text-stone-500"}`}>
        <span className={`inline-block w-2 h-2 ${ok ? "bg-emerald-500" : "bg-stone-300"}`} />{children}
    </li>
);
